import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { isNullOrUndefined } from '@sla-ng/core';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { IProductionVersionLayout } from '../../../models/layout/production-version-layout.model';
import { IProductionVersion } from '../../../models/production-version.model';
import { ProductionVersionFacade } from '../../../services/facades/external-data/production-version-facade';
import { IProductionVersionState } from '../../../services/facades/external-data/production-version.state.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { ProductionVersionLayoutService } from '../../../services/layout/production-version-layout.service';

@Component({
    templateUrl: './production-version.page.html',
    styleUrls: [ './production-version.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductionVersionPage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    public layout: IProductionVersionLayout;

    public IProductionVersion: IProductionVersion;

    public dataSource: MatTableDataSource<IProductionVersion> = new MatTableDataSource<IProductionVersion>([]);

    public productionVersionState$: Observable<IProductionVersionState>;
    public sharedDataState$: Observable<ISharedDataState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private productionVersionFacade: ProductionVersionFacade,
                private myFormBuilder: FormBuilder,
                private mySharedDataFacade: SharedDataFacade,
                private myProductionVersionLayoutService: ProductionVersionLayoutService,
                private myRouter: Router,
                private myActivatedRoute: ActivatedRoute) {
    }

    // eslint-disable-next-line
    public filterFormGroup = this.myFormBuilder.group({
        textFilter: '',
        typeFilters: this.myFormBuilder.group({
            endProductsOnly: false,
            semiProductsOnly: false,
            entryArticleNr: null as string,
        }),
    });

    public ngOnInit(): void {
        this.layout = this.myProductionVersionLayoutService.getProductionVersionLayout();
        this.productionVersionState$ = this.productionVersionFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.productionVersionState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IProductionVersionState) => s.productionVersions))
            .subscribe((data: IProductionVersion[]) => {
                this.dataSource.data = data;
            });

        // Disable or enable checkbox depending on whether data is currently being loaded
        this.productionVersionState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IProductionVersionState) => s.isLoading))
            .subscribe((isLoading: boolean) => {
                // if not loading
                if (isLoading) {
                    this.filterFormGroup.controls.typeFilters.controls.endProductsOnly.disable({ emitEvent: false });
                    this.filterFormGroup.controls.typeFilters.controls.semiProductsOnly.disable({ emitEvent: false });
                } else {
                    if (!this.filterFormGroup.controls.typeFilters.controls.endProductsOnly.value) {
                        this.filterFormGroup.controls.typeFilters.controls.semiProductsOnly.enable({ emitEvent: false });
                    }

                    if (!this.filterFormGroup.controls.typeFilters.controls.semiProductsOnly.value) {
                        this.filterFormGroup.controls.typeFilters.controls.endProductsOnly.enable({ emitEvent: false });
                    }
                }
            });

        this.setupSortingAndTextFilter();
        this.setupFilter();
        void firstValueFrom(this.mySharedDataFacade.fetchCostUnits());

    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.productionVersionFacade.setFilterState(this.filterFormGroup.getRawValue());
    }

    public clearTextFilter(): void {
        this.filterFormGroup.controls.textFilter.patchValue('');
    }

    public async openProductionVersion(selectedProductionVersion: IProductionVersion): Promise<void> {
        await this.productionVersionFacade.setSelectedProductionVersion(selectedProductionVersion.id);
        void this.myRouter.navigate([ `./${selectedProductionVersion.id}` ],
            { relativeTo: this.myActivatedRoute });
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    private setupFilter(): void {

        // Reload data when necessary
        this.filterFormGroup.controls.typeFilters.valueChanges.subscribe(() => {
            const filters = this.filterFormGroup.controls.typeFilters.getRawValue();
            void this.productionVersionFacade.fetchProductionVersions(filters.endProductsOnly, filters.semiProductsOnly, filters.entryArticleNr);
        });

        this.filterFormGroup.controls.textFilter.valueChanges.subscribe((filter) => {
            this.dataSource.filter = filter.toLowerCase().trim();
        });

        // Restore filter from state
        this.filterFormGroup.setValue(this.productionVersionFacade.getStateSnapshot().filter);

    }

    private setupSortingAndTextFilter(): void {
        this.sharedDataState$.pipe(
            takeUntil(this.destroy$),
        ).subscribe((state: ISharedDataState) => {

            // Set up Sorting by CostUnit
            this.dataSource.sortingDataAccessor = (productionVersion: IProductionVersion, tableColumnName: string): string | number => {
                switch (tableColumnName) {
                    case 'costUnitId':
                        return productionVersion.costUnitId in state.costUnitLookup ? state.costUnitLookup[ productionVersion.costUnitId ].name : null;
                    default:
                        return productionVersion[ tableColumnName ];
                }
            };


            // Set up Searching
            this.dataSource.filterPredicate = (productionVersion: IProductionVersion, filterString: string): boolean => {
                const filter = filterString.trim().toLowerCase();

                const costUnit = state.costUnitLookup[ productionVersion.costUnitId ];

                const costUnitNameMatch: boolean = isNullOrUndefined(costUnit) ? false : costUnit.name.trim().toLowerCase().indexOf(filter) !== -1;

                return costUnitNameMatch
                    || productionVersion.articleNr.trim().toLowerCase().indexOf(filter) !== -1
                    || productionVersion.incomingRecipeNr.toString().indexOf(filter) !== -1
                    || productionVersion.outgoingRecipeNr.toString().indexOf(filter) !== -1;
            };

        });
    }
}
